import React, { useState, useEffect } from 'react';
import { Modal, Button, Avatar, Input, Checkbox, Tag, Divider, message, Select } from 'antd';
import {
  ClockCircleOutlined,
  LinkOutlined,
  EnvironmentOutlined,
  CopyOutlined,
  UserAddOutlined,
  CloseOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { postData, putData } from '../../../actions/developer';
import { getData } from '../../../actions/apiCall';
import axios from 'axios';
import { getAccess } from '../../../actions/auth';

type InterviewStatus = 'selected' | 'rejected' | 'upcoming';

export interface TimelineEvent {
  id: number | string;
  interviewId?: string;
  time: string;
  duration: string;
  title: string;
  status: InterviewStatus;
  candidate: { name: string; email: string; avatar?: string; _id?: string };
  interviewer: { name: string; email: string; avatar?: string; _id?: string };
  role: string;
  dateStr: string;
  meetingLink: string;
  interviewLevel?: string;
  developerId?: string;
  startTime?: string;
  endTime?: string;
}

interface InterviewScheduleModalProps {
  visible: boolean;
  onClose: () => void;
  event?: TimelineEvent;
  onEdit?: (event: TimelineEvent) => void;
  onFeedbackAdded?: () => void;
}

const InterviewScheduleModal: React.FC<InterviewScheduleModalProps> = ({
  visible,
  onClose,
  event,
  onEdit,
  onFeedbackAdded
}) => {
  const dispatch = useDispatch();
  const { user }: any = useSelector((app: any) => app?.app);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [interviewStatus, setInterviewStatus] = useState<string>('Inprogress');
  const [visibleToAll, setVisibleToAll] = useState(true);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);
  const [checkingFeedback, setCheckingFeedback] = useState(false);

  React.useEffect(() => {
    if (event) {
      const statusMap: Record<string, string> = {
        'selected': 'Selected',
        'rejected': 'Rejected',
        'upcoming': 'Inprogress'
      };
      setInterviewStatus(statusMap[event.status] || 'Inprogress');
    }
  }, [event]);

  // Check if feedback already exists for this interview and current user
  useEffect(() => {
    const checkExistingFeedback = async () => {
      if (!visible || !event) {
        setHasExistingFeedback(false);
        return;
      }

      const isValidObjectId = (id: string | number | undefined): boolean => {
        if (!id) return false;
        const idStr = String(id);
        return /^[0-9a-fA-F]{24}$/.test(idStr);
      };

      const interviewId = isValidObjectId(event.interviewId)
        ? event.interviewId
        : isValidObjectId(event.id)
          ? event.id
          : null;

      if (!interviewId || !user?._id) {
        setHasExistingFeedback(false);
        return;
      }

      setCheckingFeedback(true);
      try {
        axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
        // Check if feedback exists for this interview and current user
        // Query feedback list filtered by interviewId
        const developerId = event.developerId || event.candidate._id;
        if (!developerId) {
          setHasExistingFeedback(false);
          setCheckingFeedback(false);
          return;
        }

        const response = await dispatch(getData(`/developer-feedback/${developerId}/get-feedback?interviewId=${interviewId}`));

        if (response?.code === 200 && response?.data) {
          const feedbacks = Array.isArray(response.data) ? response.data : response.data.data || [];
          // Check if current user has already submitted feedback for this interview
          const userFeedback = feedbacks.find((fb: any) =>
            (fb.interviewId === interviewId || fb.interviewId?._id === interviewId) &&
            (fb.addedBy?._id === user._id || fb.addedBy === user._id)
          );
          setHasExistingFeedback(!!userFeedback);
        } else {
          setHasExistingFeedback(false);
        }
      } catch (error) {
        console.error('Error checking existing feedback:', error);
        setHasExistingFeedback(false);
      } finally {
        setCheckingFeedback(false);
      }
    };

    checkExistingFeedback();
  }, [visible, event, user, dispatch]);

  if (!event) return null;

  const isCompleted = event.status === 'selected' || event.status === 'rejected';

  // Validate that interviewId is a valid MongoDB ObjectId (24 hex characters)
  const isValidObjectId = (id: string | number | undefined): boolean => {
    if (!id) return false;
    const idStr = String(id);
    // MongoDB ObjectId is 24 hex characters
    return /^[0-9a-fA-F]{24}$/.test(idStr);
  };

  // Only use interviewId if it's a valid ObjectId, otherwise show error
  const interviewId = isValidObjectId(event.interviewId)
    ? event.interviewId
    : isValidObjectId(event.id)
      ? event.id
      : null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(event.meetingLink);
    message.success('Meeting link copied to clipboard');
  };

  const handleSubmitFeedback = async (e?: React.MouseEvent<HTMLElement>) => {
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!interviewId) {
      message.error('Interview ID is missing or invalid. Please select a valid interview.');
      console.error('Invalid interviewId:', { interviewId: event.interviewId, id: event.id, event });
      return;
    }

    if (!feedbackText.trim()) {
      message.error('Please enter feedback');
      return;
    }

    // Validate rejection reason if status is rejected
    const finalStatus = interviewStatus === 'selected' || interviewStatus === 'Selected' ? 'Selected' : 'Rejected';
    if (finalStatus === 'Rejected' && !rejectionReason.trim()) {
      message.error('Please enter rejection reason');
      return;
    }

    setFeedbackLoading(true);
    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());

      // Step 1: Update interview status to "Rejected" if it's not already rejected
      // (Backend only allows feedback for rejected interviews)
      if (finalStatus === 'Rejected') {
        try {
          const updatePayload = {
            interviewStatus: 'Rejected'
          };
          const updateResponse = await dispatch(
            putData(`/contract_interview/${interviewId}`, updatePayload)
          );

          if (updateResponse?.code !== 200 && !updateResponse?._id) {
            console.warn('Failed to update interview status, but continuing with feedback submission');
          }
        } catch (updateError) {
          console.warn('Error updating interview status:', updateError);
          // Continue with feedback submission anyway
        }
      }

      // Step 2: Submit feedback
      // Use interview level directly from event (already in correct enum format)
      const getInterviewLevel = (level: string | undefined): string => {
        if (!level) return 'Level 1'; // default to first enum value
        // Return the level as-is since backend now uses proper enum values
        return level;
      };

      // Map status to backend enum format
      const mapStatus = (status: string): string => {
        const statusUpper = status.toUpperCase();
        if (statusUpper === 'SELECTED' || statusUpper === 'CLEARED') return 'CLEARED';
        if (statusUpper === 'REJECTED') return 'REJECTED';
        if (statusUpper.includes('HOLD')) return 'HOLD';
        if (statusUpper.includes('PENDING')) return 'PENDING';
        return 'PENDING'; // default
      };

      const payload = {
        developer: event.developerId || event.candidate._id,
        interviewLevel: getInterviewLevel(event.interviewLevel),
        status: mapStatus(finalStatus),
        comments: feedbackText,
        ...(finalStatus === 'Rejected' && rejectionReason ? { rejectionReason } : {})
      };

      const response = await dispatch(
        postData(`/calendar/interviews/${interviewId}/feedback`, payload)
      );

      if (response?.success || response?.code === 200 || response?.code === 201) {
        message.success('Feedback submitted successfully!');
        setFeedbackText('');
        setRejectionReason('');
        setHasExistingFeedback(true); // Hide the feedback form after successful submission
        if (onFeedbackAdded) {
          onFeedbackAdded();
        }
        onClose();
      } else {
        const errorMsg = response?.message || 'Failed to submit feedback';
        message.error(errorMsg);
        console.error('Feedback submission error:', response);
      }
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to submit feedback';
      message.error(errorMessage);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(event);
    }
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined className="text-gray-400" />}
      width={520}
      title={<span className="text-[18px] font-bold text-[#1D2939]">Interview Schedule</span>}
      className="pixel-perfect-modal"
    >
      <div className="flex flex-col gap-5 pt-2">
        {/* Interview Scheduled By */}
        <section>
          <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">
            Interview Scheduled by
          </p>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Avatar size={40} src={event.interviewer.avatar} className="border border-gray-100" />
              <div>
                <div className="text-[14px] font-bold text-gray-900">{event.interviewer.name}</div>
                <div className="text-[12px] text-gray-500">{event.interviewer.email}</div>
              </div>
            </div>
            <div className="flex gap-2">
              {!isCompleted && onEdit && (
                <Button
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  className="rounded-md px-4 h-8"
                >
                  Edit
                </Button>
              )}
              <Button type="primary" className="bg-[#1677ff] rounded-md px-6 h-8 font-semibold">
                Join
              </Button>
            </div>
          </div>
        </section>

        <Divider className="my-0 border-gray-100 border-dashed" />

        {/* Interview Details */}
        <section>
          <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3">
            Interview Detail
          </p>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <p className="text-[11px] text-gray-400 mb-1">Candidate</p>
              <div className="flex items-center gap-2">
                <Avatar size={32} src={event.candidate.avatar} />
                <div className="overflow-hidden">
                  <div className="text-[13px] font-bold truncate">{event.candidate.name}</div>
                  <div className="text-[11px] text-gray-500 truncate">{event.candidate.email}</div>
                </div>
              </div>
            </div>
            <div className="col-span-3">
              <p className="text-[11px] text-gray-400 mb-1">Role</p>
              <p className="text-[13px] font-bold text-gray-900">{event.role}</p>
            </div>
            <div className="col-span-3">
              {isCompleted && (
                <>
                  <p className="text-[11px] text-gray-400 mb-1">Status</p>
                  <Tag
                    color={event.status === 'selected' ? 'success' : 'error'}
                    className="m-0 uppercase font-bold text-[10px] px-2"
                  >
                    {event.status}
                  </Tag>
                </>
              )}
            </div>
            <div className="col-span-2 flex justify-end items-start">
              <Tag className="bg-blue-50 text-blue-600 border-none font-bold text-[10px] m-0">
                {event.interviewLevel || 'Interview'}
              </Tag>
            </div>
          </div>
        </section>

        {/* Time and Date */}
        <section className="flex items-center gap-3 text-gray-600">
          <ClockCircleOutlined className="text-gray-400" />
          <span className="text-[13px] font-medium text-gray-500">From</span>
          <div className="bg-gray-50 px-3 py-1 rounded text-[13px] font-semibold text-gray-900">
            {event.startTime || event.time || 'N/A'}
          </div>
          <span className="text-[13px] font-medium text-gray-500">to</span>
          <div className="bg-gray-50 px-3 py-1 rounded text-[13px] font-semibold text-gray-900">
            {event.endTime || 'N/A'}
          </div>
          <span className="text-[13px] font-medium text-gray-400 ml-2">{event.dateStr}</span>
        </section>

        {/* Meeting Link */}
        <section className="flex items-center gap-3">
          <LinkOutlined className="text-gray-400" />
          <div
            className="flex-1 bg-blue-50/50 border border-blue-100 rounded-md px-3 py-1.5 flex justify-between items-center group cursor-pointer"
            onClick={handleCopyLink}
          >
            <div className="flex items-center gap-2 truncate">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/9/9b/Google_Meet_icon_%282020%29.svg"
                alt="Meet"
                className="w-4 h-4"
              />
              <span className="text-[12px] text-blue-600 font-medium truncate">
                {event.meetingLink}
              </span>
            </div>
            <CopyOutlined className="text-gray-400 group-hover:text-blue-500 text-[12px]" />
          </div>
        </section>

        {/* Location */}
        <section className="flex items-center gap-3 text-gray-600 mb-2">
          <EnvironmentOutlined className="text-gray-400" />
          <span className="text-[13px] font-bold text-gray-800">Virtual</span>
        </section>

        {/* Invited People */}
        <section>
          <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3">
            Invited People
          </p>
          <div className="space-y-3 max-h-[140px] overflow-y-auto pr-1">
            {/* Candidate */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Avatar size={32} src={event.candidate.avatar}>
                  {!event.candidate.avatar && event.candidate.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <div className="text-[13px] font-bold">{event.candidate.name}</div>
                  <div className="text-[11px] text-gray-500">{event.candidate.email}</div>
                </div>
              </div>
              {isCompleted && (
                <Tag className="text-[10px] font-medium border-none px-2 bg-blue-50 text-blue-400">
                  Attended
                </Tag>
              )}
            </div>
            {/* Interviewer */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Avatar size={32} src={event.interviewer.avatar}>
                  {!event.interviewer.avatar && event.interviewer.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <div className="text-[13px] font-bold">{event.interviewer.name}</div>
                  <div className="text-[11px] text-gray-500">{event.interviewer.email}</div>
                </div>
              </div>
              {isCompleted && (
                <Tag className="text-[10px] font-medium border-none px-2 bg-blue-50 text-blue-400">
                  Attended
                </Tag>
              )}
            </div>
          </div>
        </section>

        {/* Feedback Section */}
        {!hasExistingFeedback && (
          <section className="bg-gray-50/50 -mx-6 px-6 py-4 border-t border-gray-100">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[13px] font-bold text-gray-800">
                {isCompleted ? 'Add your feedback' : 'Update Interview Status'}
              </span>
            </div>

            {!isCompleted && (
              <div className="mb-3">
                <p className="text-[11px] text-gray-400 mb-1">Interview Status</p>
                <Select
                  value={interviewStatus}
                  onChange={setInterviewStatus}
                  style={{ width: '100%' }}
                  className="text-[12px]"
                >
                  <Select.Option value="Inprogress">In Progress</Select.Option>
                  <Select.Option value="selected">Selected</Select.Option>
                  <Select.Option value="rejected">Rejected</Select.Option>
                  <Select.Option value="Response awaiting">Response Awaiting</Select.Option>
                </Select>
              </div>
            )}

            {(interviewStatus === 'rejected' || event.status === 'rejected') && (
              <div className="mb-3">
                <p className="text-[11px] text-gray-400 mb-1">Reason for Rejection</p>
                <Input
                  placeholder="Write reason"
                  className="text-[12px] py-1.5"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </div>
            )}

            <div className="mb-3">
              <p className="text-[11px] text-gray-400 mb-1">Feedback/Comments</p>
              <Input.TextArea
                placeholder="Write your feedback here..."
                rows={3}
                className="text-[12px] resize-none"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
            </div>

            <div className="flex justify-between items-center">
              <Checkbox
                checked={visibleToAll}
                onChange={(e) => setVisibleToAll(e.target.checked)}
                className="text-[11px] text-gray-500"
              >
                Visible to all
              </Checkbox>
              <div className="flex gap-2">
                <Button
                  onClick={onClose}
                  size="small"
                  className="text-[12px] px-4 h-8"
                  disabled={feedbackLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="button"
                  size="small"
                  className="text-[12px] px-6 h-8"
                  loading={feedbackLoading}
                  onClick={handleSubmitFeedback}
                >
                  {isCompleted ? 'Send Feedback' : 'Update Status'}
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
};

export default InterviewScheduleModal;